import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import TopNavMega from '@/components/landing/TopNavMega';
import BookDemoModal from '@/components/BookDemoModal';
import { WHY_CONTENT, RESOURCES_CONTENT } from '@/config/contentPages';

// ─────────────────────────────────────────────────────────────────────────────
// Universal content page for /why/:slug and /resources/:slug landing routes.
// Renders editorial content from /config/contentPages.js.
// ─────────────────────────────────────────────────────────────────────────────
export default function ContentPage({ kind }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lang, setLang] = React.useState(() => (typeof window !== 'undefined' && localStorage.getItem('reva_lang')) || 'en');
  const [demoOpen, setDemoOpen] = React.useState(false);

  const data = (kind === 'why' ? WHY_CONTENT : RESOURCES_CONTENT)[slug];
  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#FBFCFD]" data-testid={`content-${kind}-${slug}`}>
      <TopNavMega lang={lang} setLang={setLang} onDemo={() => setDemoOpen(true)} onTour={() => navigate('/')} />

      {/* Hero */}
      <section className="border-b border-[#E6EAF0] bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <button onClick={() => navigate('/')} className="text-[12px] text-[#4A5263] hover:text-[#0E1320]" data-testid="content-back">← Back to REVA</button>
          <div className="mt-6 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0E1320] text-white text-[10.5px] font-bold tracking-[0.18em] reva-mono uppercase">
            {data.eyebrow}
          </div>
          <h1 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-[#0E1320] max-w-[24ch] leading-tight">
            {data.title}
          </h1>
          <p className="mt-5 text-[15.5px] text-[#4A5263] max-w-2xl leading-relaxed">
            {data.intro}
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-[#FBFCFD]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-2 gap-4">
          {data.pillars.map((p, i) => (
            <article key={p.h} className="rounded-2xl border border-[#E6EAF0] bg-white p-6" data-testid={`content-pillar-${i}`}>
              <div className="inline-flex items-center gap-2 text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">
                <span className="w-5 h-5 rounded-full bg-[#0E1320] text-white flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                Principle
              </div>
              <h3 className="mt-3 text-[18px] font-semibold text-[#0E1320]">{p.h}</h3>
              <p className="mt-2 text-[13.5px] text-[#4A5263] leading-relaxed">{p.p}</p>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white border-t border-[#E6EAF0]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-16 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">Next step</div>
            <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-[#0E1320]">{data.cta}</h2>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E6EAF0] text-[#0E1320] text-[13px] font-medium hover:border-[#0E1320] transition-colors" data-testid="content-cta-explore">
              Explore REVA <ArrowUpRight className="h-4 w-4" />
            </button>
            <button onClick={() => navigate('/login/manager')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#00734A] text-white text-[13px] font-semibold hover:bg-[#005F3D] transition-colors" data-testid="content-cta-demo">
              Request access <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="bg-[#0B1220] text-white px-6 md:px-10 py-10">
        <div className="max-w-[1100px] mx-auto flex flex-wrap items-center justify-between gap-4 text-[12px] text-white/65">
          <div className="inline-flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#1FD18A]" /> Invitation-only platform · Saudi-first</div>
          <div>© REVA. All rights reserved.</div>
        </div>
      </footer>
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} lang={lang} sourcePage={`/${kind}/${slug}`} />
    </div>
  );
}
